import React, { createContext, useCallback, useEffect, useState } from 'react';
import { cognitoService } from '../services/cognito/cognito.service';
import { storageService } from '../utils/storage.utils';
import type {
  AuthState,
  
  LoginCredentials,
  SignupData,
} from '../types';

interface AuthContextType extends AuthState {
  signIn: (credentials: LoginCredentials) => Promise<void>;
  signUp: (data: SignupData) => Promise<void>;
  confirmSignUp: (email: string, code: string) => Promise<void>;
  signInWithGoogle: () => void;
  handleOAuthCallback: (code: string, state: string) => Promise<void>;
  signOut: () => Promise<void>;
  refreshSession: () => Promise<void>;
  updateUser: (attributes: Record<string, string>) => Promise<void>;
  forgotPassword: (email: string) => Promise<void>;
  resetPassword: (email: string, code: string, newPassword: string) => Promise<void>;
  changePassword: (oldPassword: string, newPassword: string) => Promise<void>;
}

const initialState: AuthState = {
  user: null,
  tokens: null,
  isAuthenticated: false,
  isLoading: true,
  error: null,
};

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, setState] = useState<AuthState>(initialState);

  // Initialize auth state from storage
  useEffect(() => {
    initializeAuth();
  }, []);

  const initializeAuth = async () => {
    try {
      const session = await cognitoService.getCurrentSession();

      if (session) {
        setState({
          user: session.user,
          tokens: session.tokens,
          isAuthenticated: true,
          isLoading: false,
          error: null,
        });
      } else {
        setState({
          ...initialState,
          isLoading: false,
        });
      }
    } catch (error) {
      console.error('Auth initialization error:', error);
      setState({
        ...initialState,
        isLoading: false,
        error: error instanceof Error ? error.message : 'Authentication failed',
      });
    }
  };

  // ============================================
  // SIGN IN
  // ============================================

  const signIn = useCallback(async (credentials: LoginCredentials) => {
    try {
      setState((prev) => ({ ...prev, isLoading: true, error: null }));

      const { user, tokens } = await cognitoService.signIn(credentials);

      setState({
        user,
        tokens,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Sign in failed';
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: errorMessage,
      }));
      throw error;
    }
  }, []);

  // ============================================
  // SIGN UP
  // ============================================

  const signUp = useCallback(async (data: SignupData) => {
    try {
      setState((prev) => ({ ...prev, isLoading: true, error: null }));

      await cognitoService.signUp(data);

      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: null,
      }));
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Sign up failed';
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: errorMessage,
      }));
      throw error;
    }
  }, []);

  const confirmSignUp = useCallback(async (email: string, code: string) => {
    try {
      setState((prev: AuthState) => ({ ...prev, isLoading: true, error: null }));

      await cognitoService.confirmSignUp(email, code);

      setState((prev: AuthState) => ({
        ...prev,
        isLoading: false,
        error: null,
      }));
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Confirmation failed';
      setState((prev: AuthState) => ({
        ...prev,
        isLoading: false,
        error: errorMessage,
      }));
      throw error;
    }
  }, []);

  // ============================================
  // GOOGLE OAUTH
  // ============================================

  const signInWithGoogle = useCallback(() => {
    setState((prev: AuthState) => ({ ...prev, isLoading: true, error: null }));
    cognitoService.initiateGoogleLogin();
    // Note: User will be redirected, so loading state persists until callback
  }, []);

  const handleOAuthCallback = useCallback(async (code: string, state: string) => {
    try {
      setState((prev: AuthState) => ({ ...prev, isLoading: true, error: null }));

      const { user, tokens } = await cognitoService.handleOAuthCallback(code, state);

      setState({
        user,
        tokens,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'OAuth callback failed';
      setState({
        ...initialState,
        isLoading: false,
        error: errorMessage,
      });
      throw error;
    }
  }, []);

  // ============================================
  // SIGN OUT
  // ============================================

  const signOut = useCallback(async () => {
    try {
      setState((prev: AuthState) => ({ ...prev, isLoading: true }));
      await cognitoService.signOut();
      // Cognito service handles redirect, so state will reset on page reload
    } catch (error) {
      console.error('Sign out error:', error);
      // Force clear state even on error
      setState(initialState);
      storageService.clearAll();
    }
  }, []);

  // ============================================
  // TOKEN REFRESH
  // ============================================

  const refreshSession = useCallback(async () => {
    try {
      const tokens = await cognitoService.refreshTokens();
      const user = storageService.getUserData();

      if (user) {
        setState((prev: AuthState) => ({
          ...prev,
          tokens,
          user,
          isAuthenticated: true,
        }));
      }
    } catch (error) {
      console.error('Token refresh error:', error);
      setState(initialState);
      throw error;
    }
  }, []);

  // ============================================
  // USER MANAGEMENT
  // ============================================

  const updateUser = useCallback(async (attributes: Record<string, string>) => {
    try {
      await cognitoService.updateUserAttributes(attributes);

      // Refresh user data
      const updatedAttributes = await cognitoService.getUserAttributes();
      
      setState((prev: AuthState) => ({
        ...prev,
        user: prev.user
          ? {
              ...prev.user,
              name: updatedAttributes.name || prev.user.name,
              email: updatedAttributes.email || prev.user.email,
            }
          : null,
      }));
    } catch (error) {
      throw error;
    }
  }, []);

  // ============================================
  // PASSWORD MANAGEMENT
  // ============================================

  const forgotPassword = useCallback(async (email: string) => {
    try {
      await cognitoService.forgotPassword(email);
    } catch (error) {
      throw error;
    }
  }, []);

  const resetPassword = useCallback(
    async (email: string, code: string, newPassword: string) => {
      try {
        await cognitoService.resetPassword({ email, code, newPassword });
      } catch (error) {
        throw error;
      }
    },
    []
  );

  const changePassword = useCallback(
    async (oldPassword: string, newPassword: string) => {
      try {
        await cognitoService.changePassword(oldPassword, newPassword);
      } catch (error) {
        throw error;
      }
    },
    []
  );

  const value: AuthContextType = {
    ...state,
    signIn,
    signUp,
    confirmSignUp,
    signInWithGoogle,
    handleOAuthCallback,
    signOut,
    refreshSession,
    updateUser,
    forgotPassword,
    resetPassword,
    changePassword,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};